/**
 * EvidencePanel Component (Issue #175)
 *
 * Displays code evidence for AI-generated diagram nodes.
 * Shows the actual code snippets that led to element detection.
 * Issue #404: Extended to show E2E step-specific details
 */

import { useState, useMemo } from 'react';
import type { CodeEvidence } from '@veodiagram/shared';

// Issue #404: E2E step data type for showing full node details
interface E2EStepData {
  stepType: string;
  method?: string;
  endpoint?: string;
  operation?: string;
  table?: string;
  model?: string;
  databaseStepData?: Record<string, unknown>;
  apiCallStepData?: Record<string, unknown>;
  apiRouteStepData?: Record<string, unknown>;
  backendLogicStepData?: Record<string, unknown>;
  sourceFile?: string;
  sourceLine?: number;
}

interface NodeWithEvidence {
  id: string;
  label: string;
  type?: string;
  description?: string;
  evidence?: CodeEvidence[];
  e2eStepData?: E2EStepData;
}

interface EvidencePanelProps {
  node: NodeWithEvidence | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Get programming language from file extension
 */
function getLanguageFromPath(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase();
  const langMap: Record<string, string> = {
    ts: 'typescript',
    tsx: 'typescript',
    js: 'javascript',
    jsx: 'javascript',
    py: 'python',
    go: 'go',
    rs: 'rust',
    java: 'java',
    kt: 'kotlin',
    rb: 'ruby',
    php: 'php',
    cs: 'csharp',
    cpp: 'cpp',
    c: 'c',
    h: 'c',
    hpp: 'cpp',
    sql: 'sql',
    json: 'json',
    yaml: 'yaml',
    yml: 'yaml',
    md: 'markdown',
    prisma: 'prisma',
    graphql: 'graphql',
    gql: 'graphql',
  };
  return langMap[ext || ''] || 'plaintext';
}

/**
 * Get confidence color class
 */
function getConfidenceColor(confidence?: number): string {
  if (confidence === undefined || confidence === null) return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400';
  if (confidence >= 0.8) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  if (confidence >= 0.5) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
}

// Issue #404: Step type labels for E2E flows
const STEP_TYPE_LABELS: Record<string, string> = {
  'user-action': 'User Action',
  'ui-component': 'UI Component',
  'api-call': 'API Call',
  'api-route': 'API Route',
  'backend-logic': 'Backend Logic',
  'service-call': 'Service Call',
  'database': 'Database',
  'external-api': 'External API',
  'state-change': 'State Change',
  'navigation': 'Navigation',
  'navigation-target': 'Navigation Target',
  'modal-container': 'Modal Container',
  'modal-outcome': 'Modal Outcome',
  'prop-callback': 'Prop Callback',
  'api-response': 'API Response',
  'api-callback': 'API Callback',
  'dom-update': 'DOM Update',
  'error': 'Error',
  'cache': 'Cache',
};

// Issue #404: Step type colors for badges
const STEP_TYPE_COLORS: Record<string, string> = {
  'user-action': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  'ui-component': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  'api-call': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  'api-route': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  'backend-logic': 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  'service-call': 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  'database': 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  'external-api': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  'state-change': 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  'navigation': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

/**
 * Issue #404: Detail row component for step data
 */
function DetailRow({ label, value }: { label: string; value: string | string[] | undefined }) {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;
  const displayValue = Array.isArray(value) ? value.join(', ') : value;
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-slate-500 dark:text-slate-400 flex-shrink-0">{label}:</span>
      <span className="text-slate-700 dark:text-slate-300 font-mono text-xs break-all">{displayValue}</span>
    </div>
  );
}

/**
 * Issue #404: API Call step details
 */
function ApiCallDetails({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="space-y-1">
      <DetailRow label="Method" value={data.method as string} />
      <DetailRow label="Endpoint" value={data.endpoint as string} />
      <DetailRow label="Library" value={data.library as string} />
      <DetailRow label="Path Params" value={data.pathParams as string[]} />
      <DetailRow label="Wrapper" value={data.wrapperFunction as string} />
    </div>
  );
}

/**
 * Issue #404: Database step details
 */
function DatabaseDetails({ data }: { data: Record<string, unknown> }) {
  const joins = data.joins as Array<{ table: string; type: string }> | undefined;
  const joinStr = joins?.map(j => `${j.type} ${j.table}`).join(', ');
  return (
    <div className="space-y-1">
      <DetailRow label="Operation" value={data.operation as string} />
      <DetailRow label="Table" value={data.table as string} />
      <DetailRow label="Model" value={data.model as string} />
      <DetailRow label="Library" value={data.library as string} />
      <DetailRow label="Fields" value={data.fields as string[]} />
      <DetailRow label="Where" value={data.whereFields as string[]} />
      {joinStr && <DetailRow label="Joins" value={joinStr} />}
      {Boolean(data.inTransaction) && (
        <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded">
          In Transaction
        </span>
      )}
    </div>
  );
}

/**
 * Issue #404: API Route step details
 */
function ApiRouteDetails({ data }: { data: Record<string, unknown> }) {
  const method = data.method;
  const methodStr = Array.isArray(method) ? method.join(', ') : method as string;
  return (
    <div className="space-y-1">
      <DetailRow label="Method" value={methodStr} />
      <DetailRow label="Path" value={data.path as string} />
      <DetailRow label="Handler" value={data.handlerName as string} />
      <DetailRow label="Framework" value={data.framework as string} />
      <DetailRow label="Middleware" value={data.middleware as string[]} />
      <DetailRow label="Request Fields" value={data.requestFields as string[]} />
      <DetailRow label="Response Fields" value={data.responseFields as string[]} />
      <DetailRow label="Path Params" value={data.pathParams as string[]} />
    </div>
  );
}

/**
 * Issue #404: Backend Logic step details
 */
function BackendLogicDetails({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="space-y-1">
      <DetailRow label="Function" value={data.functionName as string} />
      <DetailRow label="Logic Type" value={data.logicType as string} />
      <DetailRow label="Operations" value={data.operations as string[]} />
      <DetailRow label="Input Fields" value={data.inputFields as string[]} />
      <DetailRow label="Output Fields" value={data.outputFields as string[]} />
    </div>
  );
}

/**
 * Issue #404: E2E Step Details component - shows full step information
 */
function E2EStepDetails({ stepData }: { stepData: E2EStepData }) {
  const stepTypeLabel = STEP_TYPE_LABELS[stepData.stepType] || stepData.stepType;
  const stepTypeColor = STEP_TYPE_COLORS[stepData.stepType] || 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400';

  // Check if we have any step-specific data to show
  const hasStepSpecificData = stepData.databaseStepData || stepData.apiCallStepData ||
    stepData.apiRouteStepData || stepData.backendLogicStepData;

  // Also check for basic fields (method, endpoint, operation, table)
  const hasBasicFields = stepData.method || stepData.endpoint || stepData.operation || stepData.table;

  if (!hasStepSpecificData && !hasBasicFields && !stepData.sourceFile) {
    return null;
  }

  return (
    <div className="px-4 py-3 bg-slate-50/50 dark:bg-slate-800/30 border-b border-slate-200 dark:border-slate-700">
      <div className="flex items-center gap-2 mb-3">
        <span className={`px-2 py-0.5 text-xs font-medium rounded ${stepTypeColor}`}>
          {stepTypeLabel}
        </span>
        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Step Details</span>
      </div>

      <div className="space-y-2">
        {/* Show step-specific data based on step type */}
        {stepData.stepType === 'api-call' && stepData.apiCallStepData && (
          <ApiCallDetails data={stepData.apiCallStepData} />
        )}
        {stepData.stepType === 'database' && stepData.databaseStepData && (
          <DatabaseDetails data={stepData.databaseStepData} />
        )}
        {stepData.stepType === 'api-route' && stepData.apiRouteStepData && (
          <ApiRouteDetails data={stepData.apiRouteStepData} />
        )}
        {stepData.stepType === 'backend-logic' && stepData.backendLogicStepData && (
          <BackendLogicDetails data={stepData.backendLogicStepData} />
        )}

        {/* Fallback: show basic fields if no step-specific data */}
        {!hasStepSpecificData && hasBasicFields && (
          <div className="space-y-1">
            <DetailRow label="Method" value={stepData.method} />
            <DetailRow label="Endpoint" value={stepData.endpoint} />
            <DetailRow label="Operation" value={stepData.operation} />
            <DetailRow label="Table" value={stepData.table} />
            <DetailRow label="Model" value={stepData.model} />
          </div>
        )}

        {/* Source location */}
        {stepData.sourceFile && (
          <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              <span className="font-mono">{stepData.sourceFile}</span>
              {stepData.sourceLine && <span>:{stepData.sourceLine}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * EvidenceCard - Display a single piece of code evidence
 */
function EvidenceCard({ evidence, index }: { evidence: CodeEvidence; index: number }) {
  const [isExpanded, setIsExpanded] = useState(index === 0); // First one expanded by default

  const language = useMemo(() => getLanguageFromPath(evidence.filePath), [evidence.filePath]);
  const fileName = evidence.filePath.split('/').pop() || evidence.filePath;

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 flex items-center justify-between gap-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
      >
        <div className="flex items-center gap-2 text-sm min-w-0">
          <span className="text-slate-400 flex-shrink-0">
            {isExpanded ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </span>
          <span className="font-mono text-xs text-slate-600 dark:text-slate-300 truncate">
            {fileName}
          </span>
          <span className="text-xs text-slate-400 flex-shrink-0">
            L{evidence.lineStart}
            {evidence.lineEnd !== evidence.lineStart && `-${evidence.lineEnd}`}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {evidence.confidence !== undefined && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${getConfidenceColor(evidence.confidence)}`}>
              {Math.round(evidence.confidence * 100)}%
            </span>
          )}
        </div>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="border-t border-slate-200 dark:border-slate-700">
          {/* Full path */}
          <div className="px-3 py-1.5 bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              <span className="font-mono break-all">{evidence.filePath}</span>
            </div>
          </div>

          {/* Code snippet */}
          <div className="relative">
            <pre className="p-3 text-xs overflow-x-auto bg-slate-900 dark:bg-slate-950">
              <code className={`language-${language} text-slate-100`}>
                {(evidence.snippet || '').split('\n').map((line, i) => (
                  <div key={i} className="flex">
                    <span className="w-8 text-slate-500 select-none text-right pr-3">
                      {evidence.lineStart + i}
                    </span>
                    <span className="flex-1">{line || ' '}</span>
                  </div>
                ))}
              </code>
            </pre>
            {/* Copy button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(evidence.snippet || '');
              }}
              className="absolute top-2 right-2 p-1.5 bg-slate-700/80 hover:bg-slate-600 rounded text-slate-300 hover:text-white transition-colors"
              title="Copy code"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
              </svg>
            </button>
          </div>

          {/* Matched pattern */}
          {evidence.matchedPattern && (
            <div className="px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border-t border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-500 dark:text-slate-400">Matched:</span>
                <code className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded font-mono">
                  {evidence.matchedPattern}
                </code>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * EvidencePanel - Main panel component
 */
export function EvidencePanel({ node, isOpen, onClose }: EvidencePanelProps) {
  if (!isOpen || !node) return null;

  const hasEvidence = node.evidence && node.evidence.length > 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-[480px] max-w-full bg-white dark:bg-slate-900 shadow-xl z-50 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            Code Evidence
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Node info */}
        <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              {/* Issue #404: Show full label without truncation */}
              <h4 className="font-medium text-slate-800 dark:text-slate-200 break-words">
                {node.label}
              </h4>
              {node.type && (
                <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded">
                  {node.type}
                </span>
              )}
              {node.description && (
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {node.description}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Issue #404: E2E Step Details section */}
        {node.e2eStepData && <E2EStepDetails stepData={node.e2eStepData} />}

        {/* Evidence list */}
        <div className="flex-1 overflow-y-auto p-4">
          {hasEvidence ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  {node.evidence!.length} evidence source{node.evidence!.length !== 1 ? 's' : ''} found
                </span>
              </div>
              {node.evidence!.map((evidence, index) => (
                <EvidenceCard key={index} evidence={evidence} index={index} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-base font-medium text-slate-600 dark:text-slate-400 mb-1">
                No Evidence Available
              </h4>
              <p className="text-sm text-slate-500 dark:text-slate-500 max-w-xs">
                This node doesn't have code evidence attached. Evidence is generated by AI analysis.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {hasEvidence && (
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex gap-2">
            <button
              onClick={() => {
                const evidenceText = node.evidence!
                  .map(e => `File: ${e.filePath}\nLines: ${e.lineStart}-${e.lineEnd}\n${e.snippet}`)
                  .join('\n\n---\n\n');
                navigator.clipboard.writeText(evidenceText);
              }}
              className="flex-1 px-3 py-2 text-sm font-medium bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
              </svg>
              Copy All Evidence
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default EvidencePanel;
